import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { alpha, Box, TextField, TextFieldProps } from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { useDebouncedCallback } from "use-debounce";

const fadeIn = keyframes`
  from {
    opacity: 0.7;
  }
  to {
    opacity: 1;
  }
`;

const optimisticGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
  50% {
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

type SmartTextFieldWrapperProps = {
  ismodifying: number;
  ispending: number;
  iserror: number;
  issuccess: number;
};

const SmartTextFieldWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "ismodifying" && prop !== "ispending" && prop !== "iserror" && prop !== "issuccess",
})<SmartTextFieldWrapperProps>(({ theme, ismodifying, ispending, iserror, issuccess }) => ({
  width: "100%",
  transition: "all 0.3s ease",
  position: "relative",

  "& .MuiInputBase-root": {
    transition: "all 0.3s ease-in-out",
  },

  "& .MuiFormHelperText-root": {
    position: "absolute",
    marginTop: "2px",
    opacity: 1,
    transition: "opacity 0.3s ease-in-out",
  },

  ...(ismodifying && {
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      height: "2px",
      background: theme.palette.primary.main,
      animation: `${fadeIn} 0.3s ease-in-out`,
      zIndex: 2,
      width: "100%",
    },
  }),

  ...(ispending && {
    animation: `${optimisticGlow} 2s ease-in-out infinite`,
    "& .MuiInputBase-root": {
      backgroundColor: "rgba(76, 175, 80, 0.02)",
    },
  }),

  // Error state glow
  ...(iserror && {
    "&::before": {
      content: '""',
      position: "absolute",
      inset: -2,
      borderRadius: 1,
      pointerEvents: "none",
      background: alpha(theme.palette.error.main, 0.3),
      boxShadow: `0 0 12px ${alpha(theme.palette.error.main, 0.5)}`,
      transition: "all 0.3s ease-in-out",
    },
  }),

  // Success state glow
  ...(issuccess && {
    "&::before": {
      content: '""',
      position: "absolute",
      inset: -2,
      borderRadius: 1,
      pointerEvents: "none",
      background: alpha(theme.palette.success.main, 0.3),
      boxShadow: `0 0 12px ${alpha(theme.palette.success.main, 0.5)}`,
      transition: "all 0.3s ease-in-out",
    },
  }),
}));

/**
 * Enhanced text field with built-in debouncing and optimistic UI feedback.
 *
 * Visual states:
 *  • Blue underline while the user is actively typing (`isModifying`).
 *  • Green glow when a debounced `onChangeCommitted` callback has fired and the
 *    parent component is presumed to be saving (`isPending`).
 *  • Red glow when error is true
 *  • Green glow when isSuccess is true (temporary)
 *
 * The pending state automatically clears after 2 seconds or when the user types
 * again. The component is a drop-in replacement for MUI's `TextField`.
 */
export type SmartTextFieldProps = {
  value?: string | number;
  onChange?: (value: string) => void;
  onChangeCommitted?: (value: string) => void;
  debounceTime?: number;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  testId?: string;
  endAdornment?: React.ReactNode;
  smallAdornment?: boolean;
  externalPending?: boolean; // Allow external control of pending state
  selectOnFocus?: boolean; // Auto-select all text when clicking/focusing
  isSuccess?: boolean; // External success state
  decimal?: {
    enabled: boolean;
    decimals?: number;
    formatOnBlur?: boolean;
    preserveTrailingZeros?: boolean;
  };
} & Omit<TextFieldProps, "onChange">;

// Utility functions for decimal handling
const formatDecimal = (val: string | number, decimals: number): string => {
  if (val === "" || val === null || val === undefined) return "";
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num)) return String(val);
  return num.toFixed(decimals);
};

const parseDecimal = (val: string): number | null => {
  if (!val || val === "") return null;
  const num = parseFloat(val);
  return isNaN(num) || !isFinite(num) ? null : num;
};

const SmartTextField = forwardRef<HTMLDivElement, SmartTextFieldProps>((props, ref) => {
  const {
    value,
    onChange,
    onChangeCommitted,
    debounceTime = 300,
    onBlur,
    testId,
    endAdornment,
    smallAdornment,
    externalPending = false,
    selectOnFocus = true, // Default to true for better UX
    isSuccess = false,
    error = false,
    decimal,
    ...rest
  } = props;

  // Optimize decimal handling
  const isDecimalMode = decimal?.enabled && rest.type === "number";
  const decimalPlaces = decimal?.decimals ?? 2;

  // Memoize formatted external value
  const formattedExternalValue = useMemo(() => {
    if (!isDecimalMode || !decimal?.formatOnBlur) return String(value ?? "");
    const strValue = String(value ?? "");

    // Preserve user input patterns
    if (strValue.endsWith(".") || (strValue.includes(".") && strValue.match(/\.0*$/))) {
      return strValue;
    }

    return formatDecimal(value ?? "", decimalPlaces);
  }, [value, isDecimalMode, decimal?.formatOnBlur, decimalPlaces]);

  const [localValue, setLocalValue] = useState<string>(formattedExternalValue);
  const [isModifying, setIsModifying] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Track numeric value for decimal mode
  const lastNumericValue = useRef<number | null>(parseDecimal(String(value ?? "")));

  // Smart value sync - immediate update when not focused
  useEffect(() => {
    if (isFocused) {
      // While focused, only update if external value changed significantly
      if (isDecimalMode) {
        const currentNum = parseDecimal(localValue);
        const externalNum = parseDecimal(String(value ?? ""));

        // Force update if values are significantly different
        if (currentNum !== externalNum && !(currentNum === null && externalNum === null)) {
          setLocalValue(formattedExternalValue);
          lastNumericValue.current = externalNum;
        }
      }
    } else {
      // Not focused - immediately sync with external value
      setLocalValue(formattedExternalValue);
      if (isDecimalMode) {
        lastNumericValue.current = parseDecimal(String(value ?? ""));
      }
    }
  }, [value, formattedExternalValue, isFocused]); // Removed localValue from deps to prevent loops

  // Show success glow briefly after save
  useEffect(() => {
    if (isSuccess && !externalPending) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, externalPending]);

  const debouncedOnChangeCommitted = useDebouncedCallback((value: string) => {
    // Skip if value hasn't actually changed numerically in decimal mode
    if (isDecimalMode) {
      const numValue = parseDecimal(value);
      if (
        numValue === lastNumericValue.current ||
        (numValue === null && lastNumericValue.current === null)
      ) {
        return;
      }
      lastNumericValue.current = numValue;
    }

    onChangeCommitted?.(value);
    setIsModifying(false);
    setIsPending(true);
    // Auto-clear pending state after a reasonable time
    setTimeout(() => setIsPending(false), 2000);
  }, debounceTime);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      setIsModifying(true);
      setIsPending(false); // Clear pending state on new changes
      onChange?.(newValue);
      debouncedOnChangeCommitted(newValue);
    },
    [onChange, debouncedOnChangeCommitted]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      if (isDecimalMode && decimal?.formatOnBlur) {
        // Format decimal on blur
        const numValue = parseDecimal(e.target.value);
        if (numValue !== null) {
          const formatted = formatDecimal(numValue, decimalPlaces);
          setLocalValue(formatted);
          e.target.value = formatted;
        }
      }

      // Only fire onChangeCommitted on blur if the value has changed
      const currentValue = e.target.value;
      const externalValue = String(value ?? "");

      if (isDecimalMode) {
        // Compare numeric values for decimal mode
        const currentNum = parseDecimal(currentValue);
        const externalNum = parseDecimal(externalValue);

        if (currentNum !== externalNum && !(currentNum === null && externalNum === null)) {
          debouncedOnChangeCommitted.cancel();
          lastNumericValue.current = currentNum;
          onChangeCommitted?.(currentValue);
          setIsModifying(false);
          setIsPending(true);
          setTimeout(() => setIsPending(false), 2000);
        }
      } else if (isModifying && currentValue !== externalValue) {
        // String comparison for non-decimal mode
        debouncedOnChangeCommitted.cancel();
        onChangeCommitted?.(currentValue);
        setIsModifying(false);
        setIsPending(true);
        setTimeout(() => setIsPending(false), 2000);
      }

      onBlur?.(e);
    },
    [
      isDecimalMode,
      decimal?.formatOnBlur,
      decimalPlaces,
      value,
      isModifying,
      debouncedOnChangeCommitted,
      onChangeCommitted,
      onBlur,
    ]
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);

      if (selectOnFocus) {
        // Use setTimeout to ensure the focus event completes before selecting
        setTimeout(() => {
          // Check if the target has a select method (input elements) before calling
          if (e.target && typeof e.target.select === "function") {
            e.target.select();
          }
        }, 0);
      }
      rest.onFocus?.(e);
    },
    [selectOnFocus, rest]
  );

  // Use external pending state if provided, otherwise use internal state
  const effectivePending = externalPending || isPending;

  return (
    <SmartTextFieldWrapper
      ismodifying={isModifying ? 1 : 0}
      ispending={effectivePending ? 1 : 0}
      iserror={error ? 1 : 0}
      issuccess={showSuccess ? 1 : 0}
      ref={ref}
    >
      <TextField
        {...rest}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        data-testid={testId}
        error={error}
        InputProps={{
          ...rest.InputProps,
          ...(endAdornment && {
            endAdornment: smallAdornment ? (
              <Box
                sx={{
                  fontSize: "0.8rem",
                  minWidth: "35px",
                  position: "absolute",
                  right: 0,
                  textAlign: "right",
                }}
              >
                {endAdornment}
              </Box>
            ) : (
              endAdornment
            ),
          }),
        }}
        sx={{
          "& .MuiInput-underline:after": {
            transition: "transform 0.3s ease",
          },
          "& .MuiInput-underline.Mui-focused:after": {
            transform: "scaleX(1)",
          },
          "&:hover input+div": {
            display: "none",
          },
          ...rest.sx,
        }}
      />
    </SmartTextFieldWrapper>
  );
});

SmartTextField.displayName = "SmartTextField";

export default SmartTextField;
